import {
  useParams,
  generatePath,
  useMatch,
  useNavigate,
} from 'react-router-dom';
import { useEffect } from 'react';
import { ExchangeSidepanel } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import useProblemExchangeTransaction from './hooks/use-exchange';
import { clearTransactionState } from '../../store/slices/problem-transactions/exchange-transaction';

const ProblemExchangeTransactionHOC = ({
  backPath,
  generalPath,
  logPath,
  matchPath,
  chatPath,
  changeLogPath,
  commentsPath,
}: {
  backPath: string;
  generalPath: string;
  logPath: string;
  matchPath: string;
  chatPath: string;
  changeLogPath: string;
  commentsPath: string;
}): React.JSX.Element => {
  const { transaction } = useAppSelector(
    (state) => state.customersProblemTransactions.exchange,
  );
  const { overdraftEnabled } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { currencies: currenciesList, activePermissions } = useAppSelector(
    (state) => state.root,
  );

  const match = useMatch(matchPath);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { customerId, transactionId } = useParams();

  const {
    panelIsOpen,
    handleClose,
    search,
    logsIsLoading,
    isLoading,
    isFetching,
    setPanelIsOpen,
    getExchangeTransactionById,
  } = useProblemExchangeTransaction({
    transactionId,
    activePermissions,
    backPath,
    customerId,
    transaction,
  });

  useEffect(() => {
    if (match && customerId && transactionId) {
      navigate(
        generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      );
    }
  }, [match, customerId, transactionId]);

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearTransactionState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (customerId && !transaction && transactionId) {
      getExchangeTransactionById();
    }
  }, [getExchangeTransactionById, customerId, transaction, transactionId]);

  return (
    <ExchangeSidepanel
      panelIsOpen={panelIsOpen}
      handleClose={handleClose}
      tabs={{
        generalPath: generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
        logPath: generatePath(`${logPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
        changeLogPath: generatePath(`${changeLogPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
        chatPath: generatePath(`${chatPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
        commentsPath: generatePath(`${commentsPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      }}
      isLoading={logsIsLoading || isLoading || isFetching}
      title="Exchange Transaction"
      currenciesList={currenciesList}
      transactionInfo={transaction}
      overdraftEnabled={overdraftEnabled}
      sidepanelExpandedKey="problem-exchange"
    />
  );
};

export default ProblemExchangeTransactionHOC;
